import { H2, P, Ul, Ol, Code, CodeBlock, DocTable } from '../../components/docs/primitives';
import { SPEC } from './links';

const FRAME = `Content-Length: <byte count>\\r\\n
\\r\\n
<exactly that many bytes of UTF-8 JSON>`;

const INITIALIZE = `{
  "protocol_version": 1,
  "host_version": "8.0.0",
  "plugin_id": "yourhandle.my-plugin",
  "granted": {
    "events": ["on_watch_tick", "on_followed_live"],
    "host_methods": ["get_followed_live", "notify", "log"],
    "credentials": [],
    "ui": []
  },
  "data_dir": "<absolute path for persistent state>",
  "log_dir": "<absolute path of the log directory>"
}`;

export default function Protocol() {
  return (
    <>
      <H2 id="transport">Transport</H2>
      <Ul>
        <li>
          One JSON-RPC 2.0 envelope per frame, with a byte-length header. Batch arrays are not
          supported.
        </li>
        <li>Maximum frame size is 4 MiB; an oversized or malformed frame may get you terminated.</li>
        <li>
          Requests carry an <Code>id</Code> and get exactly one response. Notifications carry no{' '}
          <Code>id</Code> and get no response, not even an error.
        </li>
        <li>stdin and stdout are protocol-only. stderr is captured to your log file.</li>
      </Ul>
      <CodeBlock code={FRAME} />

      <H2 id="lifecycle">Lifecycle</H2>
      <Ol>
        <li>
          The host spawns your executable and sends the <Code>initialize</Code> request. Respond
          within 10 seconds or be terminated.
        </li>
        <li>
          The host sends the <Code>initialized</Code> notification. Event delivery begins only
          after this point.
        </li>
        <li>
          On shutdown the host sends a <Code>shutdown</Code> request, then an <Code>exit</Code>{' '}
          notification. Exit with code 0; anything alive 5 seconds later is killed.
        </li>
      </Ol>
      <P>
        <Code>granted</Code> in the initialize params is the subset of your manifest the user
        approved. Your reply lists the events you want delivered (a subset of the granted set).
      </P>
      <CodeBlock code={INITIALIZE} />
      <P>
        Health: a <Code>ping</Code> request arrives every 30 seconds; respond with{' '}
        <Code>{'{}'}</Code>. Three misses count as unhealthy. Crashed or unhealthy plugins restart
        with backoff (1s, 5s, 25s), and more than 3 restarts in 10 minutes disables the plugin and
        notifies the user. Events missed while down are not replayed; rebuild state from host
        methods after every start.
      </P>

      <H2 id="events">Events</H2>
      <P>
        Delivered as notifications, only for events you subscribed to. Channel objects carry{' '}
        <Code>channel_id</Code>, <Code>login</Code>, <Code>display_name</Code>, and nullable game,
        start time, and viewer count fields. Timestamps are RFC 3339 UTC.
      </P>
      <DocTable
        head={['Event', 'When it fires']}
        rows={[
          [<Code>on_stream_start</Code>, 'The user started playback of a channel.'],
          [<Code>on_stream_stop</Code>, 'Playback of that channel stopped.'],
          [
            <Code>on_channel_change</Code>,
            'The active on-screen channel changed (switch, or focus change in the multi-stream grid).',
          ],
          [
            <Code>on_watch_tick</Code>,
            'Periodic tick, nominally every 60 seconds. Carries the active channel id, null when nothing is playing. Do not assume exact cadence.',
          ],
          [
            <Code>on_followed_live</Code>,
            'The set of live followed channels, after startup and whenever the host refreshes it.',
          ],
          [
            <Code>on_ad_window</Code>,
            'Read-only ad detection state changed for a stream the app is playing.',
          ],
          [
            <Code>on_panel_change</Code>,
            "The user changed values in the plugin's settings panel.",
          ],
        ]}
      />

      <H2 id="host-methods">Host methods</H2>
      <P>
        Every method is gated: calling outside your granted set fails with{' '}
        <Code>capability_denied</Code>.
      </P>
      <DocTable
        head={['Method', 'What it does']}
        rows={[
          [<Code>get_followed_live</Code>, 'Returns the current list of live followed channels.'],
          [
            <Code>set_upstream</Code>,
            "Replaces the upstream playlist the host's local relay serves for a stream. How a resolution-owning plugin feeds the player.",
          ],
          [
            <Code>get_credential</Code>,
            'Requests a credential by kind. First call per session may block up to 120 seconds for the user prompt; every handover is audit-logged.',
          ],
          [
            <Code>notify</Code>,
            'Shows a user-facing notification attributed to the plugin. Rate limited: burst of 3, then one per 10 seconds.',
          ],
          [<Code>log</Code>, 'Notification, no response. Appends to your log file.'],
          [
            <Code>register_panel</Code>,
            'Registers or replaces your settings panel from a schema (toggle, number, select, text, string_list fields). The host renders and persists it.',
          ],
          [<Code>get_panel_values</Code>, "Returns the panel's current values."],
        ]}
      />

      <H2 id="errors">Errors</H2>
      <P>
        Standard JSON-RPC 2.0 codes apply. Host-defined codes arrive with{' '}
        <Code>error.data.name</Code> and an optional <Code>retry_after_ms</Code>:
      </P>
      <DocTable
        head={['Code', 'Name', 'Meaning']}
        rows={[
          ['-32000', <Code>capability_denied</Code>, 'Method or credential kind not granted.'],
          ['-32001', <Code>consent_denied</Code>, 'User declined the prompt, or revoked the grant.'],
          ['-32002', <Code>unknown_stream</Code>, 'No active relay session matches the stream id.'],
          ['-32003', <Code>rate_limited</Code>, 'Too many calls; retry later.'],
          ['-32004', <Code>shutting_down</Code>, 'Host is shutting down; the call was not performed.'],
          ['-32005', <Code>credential_unavailable</Code>, 'No credential of that kind exists to hand over.'],
        ]}
      />

      <H2 id="versioning">Versioning</H2>
      <Ul>
        <li>
          <Code>protocol_version</Code> is an integer, currently 1, bumped only on breaking
          changes. New events, methods, optional fields, and error codes are additive and do not
          bump it.
        </li>
        <li>Both sides must ignore unknown fields in any payload.</li>
        <li>
          The manifest's <Code>host_min</Code> gates installation against the app version;{' '}
          <Code>protocol_version</Code> gates the wire contract at the handshake.
        </li>
      </Ul>
      <P>
        The frozen contract, including full payload shapes, lives in{' '}
        <a href={SPEC.protocol} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          PROTOCOL.md
        </a>
        .
      </P>
    </>
  );
}
